import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './api'

export interface HealthCheck {
  id: string
  service_id: string
  service_name: string
  name: string
  url_path: string
  method: 'GET' | 'POST' | 'HEAD'
  port: number | null
  expect_min: number
  expect_max: number
  interval_s: number
  timeout_s: number
  auth_header_name: string | null
  auth_header_set: boolean
  enabled: boolean
  consecutive_failures: number
  last_probe_at: string | null
  last_status: number | null
  last_error_text: string | null
  auto_disabled_at: string | null
}

export interface HealthCheckCreate {
  service_id: string
  name: string
  url_path?: string
  method?: 'GET' | 'POST' | 'HEAD'
  port?: number | null
  expect_min?: number
  expect_max?: number
  interval_s?: number
  timeout_s?: number
  auth_header_name?: string | null
  auth_header_value?: string | null
  enabled?: boolean
}

export type HealthCheckUpdate = Partial<Omit<HealthCheckCreate, 'service_id'>>

export interface ProbeResult {
  status_code: number | null
  duration_ms: number
  error_text: string | null
  last_probe_at?: string | null
  consecutive_failures?: number
}

export interface HealthCheckHistory {
  range: '1h' | '6h' | '24h'
  uptime_pct: number
  buckets: Array<{
    ts: string | null
    ok_count: number
    fail_count: number
    uptime_pct: number
  }>
  recent: Array<{
    ts: string | null
    status_code: number | null
    duration_ms: number | null
    error_text: string | null
  }>
}

export function useHealthChecks(serviceId?: string) {
  return useQuery<HealthCheck[]>({
    queryKey: ['health-checks', serviceId ?? 'all'],
    queryFn: () => {
      const q = serviceId ? `?service_id=${encodeURIComponent(serviceId)}` : ''
      return api<HealthCheck[]>(`/api/health-checks${q}`)
    },
    refetchInterval: 15_000,
  })
}

export function useHealthCheck(id: string | undefined) {
  return useQuery<HealthCheck>({
    queryKey: ['health-check', id],
    queryFn: () => api<HealthCheck>(`/api/health-checks/${id}`),
    enabled: !!id,
    refetchInterval: 15_000,
  })
}

export function useCreateHealthCheck() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: HealthCheckCreate) =>
      api<HealthCheck>('/api/health-checks', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['health-checks'] }),
  })
}

export function useUpdateHealthCheck() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: HealthCheckUpdate }) =>
      api<HealthCheck>(`/api/health-checks/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['health-checks'] }),
  })
}

export function useDeleteHealthCheck() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api(`/api/health-checks/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['health-checks'] }),
  })
}

export function useProbeNow() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api<ProbeResult>(`/api/health-checks/${id}/probe-now`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['health-checks'] }),
  })
}

export function useTestHealthCheck() {
  return useMutation({
    mutationFn: (body: HealthCheckCreate) =>
      api<ProbeResult>('/api/health-checks/test', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  })
}

export function useCheckHistory(
  id: string | undefined,
  range: '1h' | '6h' | '24h',
) {
  return useQuery<HealthCheckHistory>({
    queryKey: ['health-check', id, 'history', range],
    queryFn: () =>
      api<HealthCheckHistory>(`/api/health-checks/${id}/history?range=${range}`),
    enabled: !!id,
    refetchInterval: 30_000,
  })
}
